import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { moduloAtivo, moduloDaRota } from './config/menuModulos';
import { ActiveModalProvider } from './contexts/ActiveModalContext';

// Importe seus componentes existentes
import LoginPage from './components/LoginPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import SuppliersPage from './components/SuppliersPage';
import ProductsPage from './components/ProductsPage';
import ProdutosFinaisPage from './components/ProdutosFinaisPage';
import EstoquePage from './components/EstoquePage';
import ProducaoPage from './components/ProducaoPage';
import PlanejamentoPage from './components/PlanejamentoPage';
import SeparacaoPesagemPage from './components/SeparacaoPesagemPage';
import RastreabilidadePage from './components/RastreabilidadePage';
import CotacoesPage from './components/CotacoesPage';
import DocumentsPage from './components/DocumentsPage';
import FornecedorDocumentosPage from './components/FornecedorDocumentosPage';
import NaoConformidadesProducaoPage from './components/NaoConformidadesProducaoPage';
import MarketplacePage from './components/MarketplacePage';
import ReceivingsPage from './components/ReceivingsPage';
import UsersPage from './components/UsersPage';
import ReportsPage from './components/ReportsPage';
import SettingsPage from './components/SettingsPage';
import CompaniesPage from './components/CompaniesPage'; // ✅ NOVA IMPORTAÇÃO
import DocumentosObrigatoriosPage from './pages/DocumentosObrigatoriosPage'; // ✅ NOVA IMPORTAÇÃO
import LeadsPage from './components/LeadsPage'; // ✅ NOVA IMPORTAÇÃO - Sistema de Leads
import SolicitacoesSistemaPage from './components/SolicitacoesSistemaPage';
import ReportIssueButton from './components/ReportIssueButton';
import CategoriasInsumosPage from './pages/CategoriasInsumosPage';
import TiposEquipamentosPage from './pages/TiposEquipamentosPage';
import SetoresPage from './pages/SetoresPage';
import FluxoSetorialPage from './pages/FluxoSetorialPage';
import RelatorioProducaoPage from './pages/RelatorioProducaoPage';
import KioskSetorPage from './components/producao/KioskSetorPage';
import TrabalhoProducaoPage from './components/producao/TrabalhoProducaoPage';
import EquipamentosPage from './components/equipamentos/EquipamentosPage';
import PlanoManutencaoPage from './components/equipamentos/PlanoManutencaoPage';
import EquipamentosPainelPage from './components/equipamentos/EquipamentosPainelPage';
import FornecedorEquipamentosPage from './components/equipamentos/FornecedorEquipamentosPage';
import CotacoesEquipamentosPage from './components/equipamentos/CotacoesEquipamentosPage';
import ArmazenamentoPage from './components/armazenamento/ArmazenamentoPage';
import ArmazemOperacaoPage from './components/armazenamento/ArmazemOperacaoPage';
import MonitoramentoPage from './components/MonitoramentoPage';
import ExpedicaoPage from './components/ExpedicaoPage';
import PainelMigracaoPage from './pages/PainelMigracaoPage';
import ArquiteturaPage from './pages/ArquiteturaPage';


// Dashboards específicos
import AdministradorDashboard from './components/AdministradorDashboard';
import FornecedorDashboard from './components/FornecedorDashboard';
import GerenteDashboard from './components/GerenteDashboard';
import RecebedorDashboard from './components/RecebedorDashboard';
import SuperadminDashboard from './components/SuperadminDashboard';

const PrivateRoute = ({ children, allowedRoles }) => {
  const auth = useAuth();
  const location = useLocation();
  
  // Debug
  console.log('🔍 PrivateRoute: auth:', auth);
  console.log('🔍 PrivateRoute: userData:', auth?.userData);
  console.log('🔍 PrivateRoute: allowedRoles:', allowedRoles);

  if (!auth) {
    return <div className="flex items-center justify-center min-h-screen">Carregando autenticação...</div>;
  }

  const { user, userData, loading, companyModulos } = auth;

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Carregando...</div>;
  }

  if (!user) {
    return <Navigate to="/app/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(userData?.funcao)) {
    console.log('🔍 PrivateRoute: Acesso negado. Role:', userData?.funcao, 'Allowed:', allowedRoles);
    return <Navigate to="/app/dashboard" replace />;
  }

  const modulo = moduloDaRota(location.pathname);
  if (userData?.funcao !== 'superadmin' && modulo) {
    if (companyModulos === undefined) {
      return <div className="flex items-center justify-center min-h-screen">Carregando acesso da empresa...</div>;
    }
    if (companyModulos && !moduloAtivo(companyModulos, modulo)) {
      return <Navigate to="/app/dashboard" replace />;
    }
  }

  return children;
};

const WebApp = () => {
  return (
    <AuthProvider>
      <ActiveModalProvider>
      <Routes>
        <Route path="login" element={<LoginPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />
        
        <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          
          {/* Rotas para Dashboards Específicos */}
          <Route path="dashboard/superadmin" element={<PrivateRoute allowedRoles={["superadmin"]}><SuperadminDashboard /></PrivateRoute>} />
          <Route path="dashboard/gerente" element={<PrivateRoute allowedRoles={["gerente"]}><GerenteDashboard /></PrivateRoute>} />
          <Route path="dashboard/administrador" element={<PrivateRoute allowedRoles={["administrador", "admin"]}><AdministradorDashboard /></PrivateRoute>} />
          <Route path="dashboard/recebedor" element={<PrivateRoute allowedRoles={["recebedor"]}><RecebedorDashboard /></PrivateRoute>} />
          <Route path="dashboard/fornecedor" element={<PrivateRoute allowedRoles={["fornecedor"]}><FornecedorDashboard /></PrivateRoute>} />

          {/* ✅ NOVA ROTA: Empresas (exclusiva para superadmin) */}
          <Route path="companies" element={<PrivateRoute allowedRoles={["superadmin"]}><CompaniesPage /></PrivateRoute>} />

          {/* ✅ NOVA ROTA: Leads (exclusiva para superadmin) */}
          <Route path="leads" element={<PrivateRoute allowedRoles={["superadmin"]}><LeadsPage /></PrivateRoute>} />

          <Route
            path="painel-migracao"
            element={
              <PrivateRoute allowedRoles={['superadmin']}>
                <PainelMigracaoPage />
              </PrivateRoute>
            }
          />
          <Route
            path="arquitetura"
            element={
              <PrivateRoute allowedRoles={['superadmin']}>
                <ArquiteturaPage />
              </PrivateRoute>
            }
          />

          {/* Solicitações de erro/ajuste reportadas pelos usuários */}
          <Route
            path="solicitacoes-sistema"
            element={
              <PrivateRoute allowedRoles={['superadmin']}>
                <SolicitacoesSistemaPage />
              </PrivateRoute>
            }
          />

          {/* ✅ NOVA ROTA: Documentos Obrigatórios (exclusiva para administradores) */}
          <Route path="documentos-obrigatorios" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin"]}><DocumentosObrigatoriosPage /></PrivateRoute>} />

          {/* Rotas para Páginas Gerais */}
          <Route
            path="suppliers"
            element={<Navigate to="/app/fornecedores/ingredientes" replace />}
          />
          <Route
            path="fornecedores"
            element={<Navigate to="/app/fornecedores/ingredientes" replace />}
          />
          <Route
            path="fornecedores/:tipoSegment"
            element={
              <PrivateRoute
                allowedRoles={['superadmin', 'gerente', 'administrador', 'admin', 'recebedor', 'qualidade']}
              >
                <SuppliersPage />
              </PrivateRoute>
            }
          />
          <Route path="products" element={<Navigate to="/app/insumos/ingredientes" replace />} />
          <Route path="insumos" element={<Navigate to="/app/insumos/ingredientes" replace />} />
          <Route
            path="insumos/materias-primas"
            element={<Navigate to="/app/insumos/ingredientes" replace />}
          />
          <Route
            path="insumos/ingredientes"
            element={
              <PrivateRoute
                allowedRoles={[
                  'superadmin',
                  'gerente',
                  'administrador',
                  'admin',
                  'recebedor',
                  'fornecedor',
                  'qualidade',
                  'producao',
                ]}
              >
                <ProductsPage insumoTipoFilter="ingrediente" />
              </PrivateRoute>
            }
          />
          <Route
            path="insumos/embalagens"
            element={
              <PrivateRoute
              allowedRoles={[
                'superadmin',
                'gerente',
                'administrador',
                'admin',
                'recebedor',
                'fornecedor',
                'qualidade',
                'producao',
              ]}
            >
                <ProductsPage insumoTipoFilter="embalagem" />
              </PrivateRoute>
            }
          />
          <Route
            path="insumos/servicos"
            element={
              <PrivateRoute
              allowedRoles={[
                'superadmin',
                'gerente',
                'administrador',
                'admin',
                'recebedor',
                'fornecedor',
                'qualidade',
                'producao',
              ]}
            >
                <ProductsPage insumoTipoFilter="servico" />
              </PrivateRoute>
            }
          />
          <Route
            path="insumos/limpeza-conservacao"
            element={
              <PrivateRoute
                allowedRoles={[
                  'superadmin',
                  'gerente',
                  'administrador',
                  'admin',
                  'recebedor',
                  'fornecedor',
                  'qualidade',
                  'producao',
                ]}
              >
                <ProductsPage insumoTipoFilter="limpeza_conservacao" />
              </PrivateRoute>
            }
          />
          <Route
            path="parametros/categorias-insumos"
            element={
              <PrivateRoute allowedRoles={['superadmin']}>
                <CategoriasInsumosPage />
              </PrivateRoute>
            }
          />
          <Route
            path="setores"
            element={
              <PrivateRoute
                allowedRoles={['superadmin', 'gerente', 'administrador', 'admin']}
              >
                <SetoresPage />
              </PrivateRoute>
            }
          />
          <Route
            path="parametros/tipos-equipamentos"
            element={
              <PrivateRoute allowedRoles={['superadmin']}>
                <TiposEquipamentosPage />
              </PrivateRoute>
            }
          />
          <Route
            path="equipamentos/painel"
            element={
              <PrivateRoute
                allowedRoles={['superadmin', 'gerente', 'administrador', 'admin', 'manutencao']}
              >
                <EquipamentosPainelPage />
              </PrivateRoute>
            }
          />
          <Route
            path="equipamentos"
            element={
              <PrivateRoute
                allowedRoles={['superadmin', 'gerente', 'administrador', 'admin', 'manutencao']}
              >
                <EquipamentosPage />
              </PrivateRoute>
            }
          />
          <Route
            path="plano-manutencao"
            element={
              <PrivateRoute
                allowedRoles={['superadmin', 'gerente', 'administrador', 'admin', 'manutencao']}
              >
                <PlanoManutencaoPage />
              </PrivateRoute>
            }
          />
          <Route
            path="equipamentos/cotacoes"
            element={
              <PrivateRoute
                allowedRoles={['superadmin', 'gerente', 'administrador', 'admin', 'manutencao']}
              >
                <CotacoesEquipamentosPage />
              </PrivateRoute>
            }
          />
          <Route
            path="fornecedor-equipamentos"
            element={
              <PrivateRoute allowedRoles={['fornecedor_equipamentos']}>
                <FornecedorEquipamentosPage />
              </PrivateRoute>
            }
          />
          <Route path="produtos-finais" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin"]}><ProdutosFinaisPage /></PrivateRoute>} />
          <Route path="estoque" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin", "producao", "qualidade"]}><EstoquePage /></PrivateRoute>} />
          <Route path="armazenamento" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin"]}><ArmazenamentoPage /></PrivateRoute>} />
          <Route path="producao" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin", "producao"]}><ProducaoPage /></PrivateRoute>} />
          <Route path="planejamento-producao" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin", "producao"]}><PlanejamentoPage /></PrivateRoute>} />
          <Route path="separacao-pesagem" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin", "producao"]}><SeparacaoPesagemPage /></PrivateRoute>} />
          <Route path="fluxo-setorial" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin", "producao", "qualidade"]}><FluxoSetorialPage /></PrivateRoute>} />
          <Route path="relatorios-producao" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin", "qualidade"]}><RelatorioProducaoPage /></PrivateRoute>} />
          <Route path="nao-conformidades-producao" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin", "producao", "qualidade"]}><NaoConformidadesProducaoPage /></PrivateRoute>} />
          <Route path="rastreabilidade" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin", "qualidade"]}><RastreabilidadePage /></PrivateRoute>} />
          <Route path="monitoramento" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin", "qualidade", "producao", "recebedor"]}><MonitoramentoPage /></PrivateRoute>} />
          <Route path="expedicao" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin", "qualidade"]}><ExpedicaoPage /></PrivateRoute>} />
          <Route path="cotacoes" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin", "fornecedor"]}><CotacoesPage /></PrivateRoute>} />
          <Route path="documents" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin", "recebedor", "fornecedor", "qualidade"]}><DocumentsPage /></PrivateRoute>} />
          <Route path="marketplace" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "fornecedor"]}><MarketplacePage /></PrivateRoute>} />
          <Route path="receivings" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin", "recebedor", "fornecedor", "qualidade"]}><ReceivingsPage /></PrivateRoute>} />
          <Route path="users" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin"]}><UsersPage /></PrivateRoute>} />
          <Route path="reports" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin"]}><ReportsPage /></PrivateRoute>} />
          <Route path="settings" element={<PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin"]}><SettingsPage /></PrivateRoute>} />

          {/* Rotas específicas do fornecedor */}
          <Route path="propostas" element={<PrivateRoute allowedRoles={["fornecedor"]}><MarketplacePage /></PrivateRoute>} />
          <Route path="fornecedor-documentos" element={<PrivateRoute allowedRoles={["fornecedor"]}><FornecedorDocumentosPage /></PrivateRoute>} />
        </Route>

        {/* Tablet de armazém: tela cheia, sem menu lateral */}
        <Route
          path="armazem-operacao"
          element={
            <PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin", "recebedor", "producao", "qualidade"]}>
              <>
                <ArmazemOperacaoPage />
                <ReportIssueButton />
              </>
            </PrivateRoute>
          }
        />

        {/* Estação de trabalho do operador: tela cheia, sem menu */}
        <Route
          path="trabalho"
          element={
            <PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin", "producao", "qualidade"]}>
              <>
                <TrabalhoProducaoPage />
                <ReportIssueButton />
              </>
            </PrivateRoute>
          }
        />

        {/* Kiosk de chão de fábrica: tela cheia, sem menu lateral */}
        <Route
          path="kiosk/:setorId"
          element={
            <PrivateRoute allowedRoles={["superadmin", "gerente", "administrador", "admin", "producao", "qualidade"]}>
              <>
                <KioskSetorPage />
                <ReportIssueButton />
              </>
            </PrivateRoute>
          }
        />

        {/* Catch-all para rotas não encontradas dentro do /app */}
        <Route path="*" element={<Navigate to="/app/login" replace />} />
      </Routes>
      </ActiveModalProvider>
    </AuthProvider>
  );
};

export default WebApp;

